import { GraphicPanel } from "./graphic-panel";
import { WebglObject } from "./webgl-object";
import { FixedDimension } from "../utils/fixed-dimension";

export class GraphicTable extends WebglObject {
  /*
  settings: {
    rowsSpace: new ScalableDimension(0, 0),
    columnsSpace: new ScalableDimension(0, 0),
    rows: [
      {
        height: new ScalableDimension(0, 0)
      }
    ],
    columns: [
      {
        width: new ScalableDimension(0, 0)
      }
    ],
    mergedCells: [
      {
        rowBegin: 0,
        rowEnd: 0,
        columnBegin: 0,
        columnEnd: 0
      }
    ]
  }
  */
  constructor(settings) {
    super(settings);

    this.settings.commonSpaceBetweenRows = 0;
    this.settings.rowsSpaceInPixels = this.settings.rowsSpace.calcValue(this.settings.clientSize.height);
    if (this.settings.rows.length > 1) {
      this.settings.commonSpaceBetweenRows = this.settings.rowsSpaceInPixels * (this.settings.rows.length - 1);
    }
    this.settings.panelsAreaHeight = this.settings.clientSize.height - this.settings.commonSpaceBetweenRows;

    this.settings.commonSpaceBetweenColumns = 0;
    this.settings.columnsSpaceInPixels = this.settings.columnsSpace.calcValue(this.settings.clientSize.width);
    if (this.settings.columns.length > 1) {
      this.settings.commonSpaceBetweenColumns = this.settings.columnsSpaceInPixels * (this.settings.columns.length - 1);
    }
    this.settings.panelsAreaWidth = this.settings.clientSize.width - this.settings.commonSpaceBetweenColumns;

    let cellY = 0;
    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const rowInfo = this.settings.rows[rowIndex];
      rowInfo.columns = [];
      const rowHeight = rowInfo.height.calcValue(this.settings.panelsAreaHeight);
      let cellX = 0;
      for (let columnIndex = 0; columnIndex < this.settings.columns.length; columnIndex++) {
        const sourceColumnInfo = this.settings.columns[columnIndex];
        const panel = new GraphicPanel(
          {
            location: {
              x: new FixedDimension(cellX),
              y: new FixedDimension(this.settings.clientSize.height - cellY - rowHeight)
            },
            size: {
              width: sourceColumnInfo.width,
              height: rowInfo.height
            },
            parent: {
              ref: this,
              isCustom: true,
              location: {
                x: this.settings.absoluteLocation.x,
                y: this.settings.absoluteLocation.y
              },
              size: {
                width: this.settings.panelsAreaWidth,
                height: this.settings.panelsAreaHeight
              },
              padding: {
                left: this.settings.paddingInPixels.left,
                right: this.settings.paddingInPixels.right,
                top: this.settings.paddingInPixels.top,
                bottom: this.settings.paddingInPixels.bottom
              }
            }
          }
        );
        const cellMergeStatus = GraphicTable.getCellMergeStatus(rowIndex, columnIndex, this.settings.mergedCells);
        const cellInfo = {
          width: sourceColumnInfo.width,
          mergeStatus: cellMergeStatus, // for merged cells info contains in left column and upper row, cells with info has merged = false
          panel: panel,
          drawablePanel: cellMergeStatus.status > 0 ? null : panel
        };
        rowInfo.columns.push(cellInfo);
        if (cellMergeStatus.status == 2) {
          const mergedCellInfo = this.settings.mergedCells[cellMergeStatus.mergedCellsIndex];
          const mergedCellStart = this.settings.rows[mergedCellInfo.rowBegin].columns[mergedCellInfo.columnBegin];
          const mergedPanelWidth = panel.settings.absoluteLocation.x + panel.settings.sizeInPixels.width - mergedCellStart.panel.settings.absoluteLocation.x;
          const mergedPanelHeight =
            mergedCellStart.panel.settings.absoluteLocation.y + mergedCellStart.panel.settings.sizeInPixels.height - panel.settings.absoluteLocation.y;

          const mergedPanel = new GraphicPanel(
            {
              location: {
                x: new FixedDimension(mergedCellStart.panel.settings.absoluteLocation.x),
                y: new FixedDimension(panel.settings.absoluteLocation.y)
              },
              size: {
                width: new FixedDimension(mergedPanelWidth),
                height: new FixedDimension(mergedPanelHeight)
              },
              parent: {
                ref: this,
                isCustom: true,
                location: {
                  x: this.settings.absoluteLocation.x,
                  y: this.settings.absoluteLocation.y
                },
                size: {
                  width: this.settings.panelsAreaWidth,
                  height: this.settings.panelsAreaHeight
                },
                padding: {
                  left: this.settings.paddingInPixels.left,
                  right: this.settings.paddingInPixels.right,
                  top: this.settings.paddingInPixels.top,
                  bottom: this.settings.paddingInPixels.bottom
                }
              }
            }
          );
          // set merged panel to left-top cell
          mergedCellStart.drawablePanel = mergedPanel;
        }
        cellX += cellInfo.width.calcValue(this.settings.panelsAreaWidth) + this.settings.columnsSpaceInPixels;
      }
      cellY += rowHeight + this.settings.rowsSpaceInPixels;
    }
    this.settings.debug.boxFillColor = [0.0, 0.8, 0, 1];
    this.settings.debug.boxStrokeColor = [0, 0.2, 0, 1];
  }

  static getCellMergeStatus(rowIndex, columnIndex, mergedCells) {
    for (let i = 0; i < mergedCells.length; i++) {
      if (
        rowIndex >= mergedCells[i].rowBegin &&
        rowIndex <= mergedCells[i].rowEnd &&
        columnIndex >= mergedCells[i].columnBegin &&
        columnIndex <= mergedCells[i].columnEnd
      ) {
        if (rowIndex == mergedCells[i].rowBegin && columnIndex == mergedCells[i].columnBegin) {
          return {
            status: 1,
            mergedCellsIndex: i
          };
        }
        if (rowIndex == mergedCells[i].rowEnd && columnIndex == mergedCells[i].columnEnd) {
          return {
            status: 2,
            mergedCellsIndex: i
          };
        }
        return {
          status: 3,
          mergedCellsIndex: i
        };
      }
    }
    return {
      status: 0,
      mergedCellsIndex: -1
    };
  }

  // height: new ScalableDimension(0, 0)
  setRowHeight(rowIndex, height) {
    // update seleted row
    let cellX = 0;
    let rowInfo = this.settings.rows[rowIndex];
    rowInfo.height = height;
    let rowHeight = rowInfo.height.calcValue(this.settings.panelsAreaHeight);
    let cellY = this.settings.clientSize.height - rowInfo.columns[0].panel.settings.locationInPixels.y;
    for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
      const cellInfo = rowInfo.columns[columnIndex];
      cellInfo.panel.setBorders(
        {
          x: new FixedDimension(cellX),
          y: new FixedDimension(this.settings.clientSize.height - cellY - rowHeight)
        },
        { width: cellInfo.panel.settings.size.width, height: height }
      );
      if (cellInfo.mergeStatus == 2) {
        const mergedCellInfo = this.settings.mergedCells[cellInfo.mergeStatus.mergedCellsIndex];
        const mergedCellStart = this.settings.rows[mergedCellInfo.rowBegin].columns[mergedCellInfo.columnBegin];
        const mergedPanelWidth =
          cellInfo.panel.settings.absoluteLocation.x + cellInfo.panel.settings.sizeInPixels.width - mergedCellStart.panel.settings.absoluteLocation.x;
        const mergedPanelHeight =
          mergedCellStart.panel.settings.absoluteLocation.y + mergedCellStart.panel.settings.sizeInPixels.height - cellInfo.panel.settings.absoluteLocation.y;
        mergedCellStart.drawablePanel.setBorders(
          {
            x: new FixedDimension(mergedCellStart.panel.settings.absoluteLocation.x),
            y: new FixedDimension(cellInfo.panel.settings.absoluteLocation.y)
          },
          {
            width: new FixedDimension(mergedPanelWidth),
            height: new FixedDimension(mergedPanelHeight)
          }
        );
      }
      cellX += cellInfo.width.calcValue(this.settings.panelsAreaWidth) + this.settings.columnsSpaceInPixels;
    }
    cellY += rowHeight + this.settings.rowsSpaceInPixels;

    // update rows below selected
    for (let r = rowIndex + 1; r < this.settings.rows.length; r++) {
      rowInfo = this.settings.rows[r];
      rowHeight = rowInfo.height.calcValue(this.settings.panelsAreaHeight);
      cellX = 0;
      for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
        const cellInfo = rowInfo.columns[columnIndex];
        this.updateCellLocation(cellInfo, cellX, cellY, rowHeight);
        cellX += cellInfo.width.calcValue(this.settings.panelsAreaWidth) + this.settings.columnsSpaceInPixels;
      }
      cellY += rowHeight + this.settings.rowsSpaceInPixels;
    }
  }

  // width: new ScalableDimension(0, 0)
  setColumnWidth(columnIndex, width) {
    let cellY = 0;
    let columnWidth = width.calcValue(this.settings.panelsAreaWidth);
    let startCellX = this.settings.rows[0].columns[columnIndex].panel.settings.locationInPixels.x + columnWidth;
    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const rowInfo = this.settings.rows[rowIndex];
      const rowHeight = rowInfo.height.calcValue(this.settings.panelsAreaHeight);
      let cellInfo = rowInfo.columns[columnIndex];
      cellInfo.panel.setSize(width, cellInfo.panel.settings.size.height);
      if (cellInfo.mergeStatus == 2) {
        const mergedCellInfo = this.settings.mergedCells[cellInfo.mergeStatus.mergedCellsIndex];
        const mergedCellStart = this.settings.rows[mergedCellInfo.rowBegin].columns[mergedCellInfo.columnBegin];
        const mergedPanelWidth =
          cellInfo.panel.settings.absoluteLocation.x + cellInfo.panel.settings.sizeInPixels.width - mergedCellStart.panel.settings.absoluteLocation.x;
        const mergedPanelHeight =
          mergedCellStart.panel.settings.absoluteLocation.y + mergedCellStart.panel.settings.sizeInPixels.height - cellInfo.panel.settings.absoluteLocation.y;
        mergedCellStart.drawablePanel.setSize(new FixedDimension(mergedPanelWidth), new FixedDimension(mergedPanelHeight));
      }

      let cellX = startCellX;
      for (let colIndex = columnIndex + 1; colIndex < rowInfo.columns.length; colIndex++) {
        cellInfo = rowInfo.columns[colIndex];
        this.updateCellLocation(cellInfo, cellX, cellY, rowHeight);
        cellX += cellInfo.width.calcValue(this.settings.panelsAreaWidth) + this.settings.columnsSpaceInPixels;
      }
      cellY += rowHeight + this.settings.rowsSpaceInPixels;
    }
  }

  addItem(rowIndex, columnIndex, item) {
    const cellInfo = this.settings.rows[rowIndex].columns[columnIndex];
    if (cellInfo.mergeStatus.status == 0) {
      cellInfo.panel.addItem(item);
    } else {
      const mergedCellInfo = this.settings.mergedCells[cellInfo.mergeStatus.mergedCellsIndex];
      const mergedCellStart = this.settings.rows[mergedCellInfo.rowBegin].columns[mergedCellInfo.columnBegin];
      mergedCellStart.drawablePanel.addItem(item);
    }
  }

  createBorders() {
    super.createBorders();
    this.settings.commonSpaceBetweenRows = 0;
    this.settings.rowsSpaceInPixels = this.settings.rowsSpace.calcValue(this.settings.clientSize.height);
    if (this.settings.rows.length > 1) {
      this.settings.commonSpaceBetweenRows = this.settings.rowsSpaceInPixels * (this.settings.rows.length - 1);
    }
    this.settings.panelsAreaHeight = this.settings.clientSize.height - this.settings.commonSpaceBetweenRows;

    this.settings.commonSpaceBetweenColumns = 0;
    this.settings.columnsSpaceInPixels = this.settings.columnsSpace.calcValue(this.settings.clientSize.width);
    if (this.settings.columns.length > 1) {
      this.settings.commonSpaceBetweenColumns = this.settings.columnsSpaceInPixels * (this.settings.columns.length - 1);
    }
    this.settings.panelsAreaWidth = this.settings.clientSize.width - this.settings.commonSpaceBetweenColumns;

    let cellY = 0;
    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const rowInfo = this.settings.rows[rowIndex];
      const rowHeight = rowInfo.height.calcValue(this.settings.panelsAreaHeight);
      let cellX = 0;
      for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
        const cellInfo = rowInfo.columns[columnIndex];
        cellInfo.panel.settings.location.x.value = cellX;
        cellInfo.panel.settings.location.y.value = this.settings.clientSize.height - cellY - rowHeight;
        cellInfo.panel.setParentSize(this.settings.panelsAreaWidth, this.settings.panelsAreaHeight);
        if (cellInfo.mergeStatus.status == 2) {
          const mergedCellInfo = this.settings.mergedCells[cellInfo.mergeStatus.mergedCellsIndex];
          const mergedCellStart = this.settings.rows[mergedCellInfo.rowBegin].columns[mergedCellInfo.columnBegin];
          const mergedPanelWidth =
            cellInfo.panel.settings.absoluteLocation.x + cellInfo.panel.settings.sizeInPixels.width - mergedCellStart.panel.settings.absoluteLocation.x;
          const mergedPanelHeight =
            mergedCellStart.panel.settings.absoluteLocation.y + mergedCellStart.panel.settings.sizeInPixels.height - cellInfo.panel.settings.absoluteLocation.y;

          mergedCellStart.drawablePanel.setBorders(
            {
              x: new FixedDimension(mergedCellStart.panel.settings.absoluteLocation.x),
              y: new FixedDimension(cellInfo.panel.settings.absoluteLocation.y)
            },
            {
              width: new FixedDimension(mergedPanelWidth),
              height: new FixedDimension(mergedPanelHeight)
            }
          );
          mergedCellStart.drawablePanel.setParentSize(this.settings.panelsAreaWidth, this.settings.panelsAreaHeight);
          mergedCellStart.drawablePanel.createBorders();
        }
        cellX += cellInfo.panel.settings.sizeInPixels.width + this.settings.columnsSpaceInPixels;
      }
      cellY += rowHeight + this.settings.rowsSpaceInPixels;
    }
  }

  setDebugMode(debugSettings) {
    super.setDebugMode(debugSettings);
    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const rowInfo = this.settings.rows[rowIndex];
      for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
        const cellInfo = rowInfo.columns[columnIndex];
        if (cellInfo.drawablePanel != null) {
          cellInfo.drawablePanel.setDebugMode(debugSettings);
        }
      }
    }
  }

  draw(gl, program) {
    super.draw(gl, program);
    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const rowInfo = this.settings.rows[rowIndex];
      for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
        const cellInfo = rowInfo.columns[columnIndex];
        if (cellInfo.drawablePanel != null) {
          cellInfo.drawablePanel.draw(gl, program);
        }
      }
    }
  }
}
